import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Folder, FolderOpen, FileText, Search, Building2, Users, User, Share2 } from 'lucide-react'
import { filesApi, type FilesFolder, type FilesObject } from '../../services/api'
import { BACKEND_URL } from '../../services/config'

interface Props {
  serverId: string
}

interface TreeState {
  folders: FilesFolder[]
  sharedObjects: FilesObject[]
}

export default function FilesTree({ serverId }: Props) {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeFolderId = searchParams.get('folder')

  const [state, setState] = useState<TreeState | null>(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!serverId) return
    filesApi.tree(serverId).then(({ data }) => {
      setState(data)
    }).catch(() => {
      setState({ folders: [], sharedObjects: [] })
    })
  }, [serverId])

  const select = (id: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('folder', id)
      return next
    })
  }

  const folders = state?.folders ?? []
  const sharedObjects = state?.sharedObjects ?? []

  const filtered = useMemo(() => {
    if (!search.trim()) return folders
    const q = search.toLowerCase()
    return folders.filter((f) => f.name.toLowerCase().includes(q))
  }, [folders, search])

  const companyRoot = filtered.find((f) => f.systemKind === 'company_root')
  const personalRoot = filtered.find((f) => f.systemKind === 'personal_root')

  // Build department groups: each dept_root + its children
  const deptRoots = filtered.filter((f) => f.systemKind === 'dept_root')
  const deptChildren = (deptRootId: string) =>
    filtered.filter(
      (f) =>
        f.parentFolderId === deptRootId &&
        (f.systemKind === 'dept_shared' || f.systemKind === 'dept_member')
    )

  // Shared-with-me: folders shared to me not under my own scopes
  const myRootIds = new Set(folders.map((f) => f.id))
  const sharedFolders = filtered.filter((f) => !myRootIds.has(f.id) && f.read && f.scope !== 'personal')

  const hasShared = sharedFolders.length > 0 || sharedObjects.length > 0

  const FolderRow = ({
    folder,
    indent = 0,
  }: {
    folder: FilesFolder
    indent?: number
  }) => {
    const active = activeFolderId === folder.id
    const Icon = active ? FolderOpen : Folder
    return (
      <button
        onClick={() => select(folder.id)}
        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors duration-150 ${
          active
            ? 'bg-primary/15 text-primary font-medium'
            : 'text-foreground hover:bg-muted'
        }`}
        style={{ paddingLeft: `${12 + indent * 16}px` }}
      >
        <Icon size={15} className="shrink-0" />
        <span className="truncate">{folder.name}</span>
      </button>
    )
  }

  const SectionLabel = ({
    label,
    icon,
  }: {
    label: string
    icon: React.ReactNode
  }) => (
    <div className="flex items-center gap-2 px-3 py-1 mt-3 mb-0.5">
      <span className="text-muted-foreground">{icon}</span>
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
    </div>
  )

  return (
    <div className="flex flex-col h-full bg-card">
      {/* Header */}
      <div className="h-14 shrink-0 px-4 flex items-center gap-2 border-b border-border">
        <Folder size={16} className="text-primary shrink-0" />
        <span className="font-semibold text-foreground">Files</span>
      </div>

      {/* Search */}
      <div className="px-3 pt-3 pb-1">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter folders…"
            className="w-full h-8 pl-8 pr-3 text-sm bg-muted rounded-md border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {/* Tree */}
      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-4">
        {/* Company */}
        {companyRoot && (
          <>
            <SectionLabel label="Company" icon={<Building2 size={13} />} />
            <FolderRow folder={companyRoot} />
          </>
        )}

        {/* Departments */}
        {deptRoots.length > 0 && (
          <>
            <SectionLabel label="Departments" icon={<Users size={13} />} />
            {deptRoots.map((dept) => (
              <div key={dept.id}>
                <FolderRow folder={dept} />
                {deptChildren(dept.id).map((child) => (
                  <FolderRow key={child.id} folder={child} indent={1} />
                ))}
              </div>
            ))}
          </>
        )}

        {/* Personal */}
        {personalRoot && (
          <>
            <SectionLabel label="Personal" icon={<User size={13} />} />
            <FolderRow folder={personalRoot} />
          </>
        )}

        {/* Shared with me */}
        {hasShared && (
          <>
            <SectionLabel label="Shared with me" icon={<Share2 size={13} />} />
            {sharedFolders.map((f) => (
              <FolderRow key={f.id} folder={f} />
            ))}
            {sharedObjects.map((obj) => (
              <a
                key={obj.id}
                href={`${BACKEND_URL}${filesApi.blobUrl(obj.id)}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors duration-150"
                style={{ paddingLeft: '12px' }}
                title={`Download ${obj.name}`}
              >
                <FileText size={15} className="shrink-0" />
                <span className="truncate">{obj.name}</span>
              </a>
            ))}
          </>
        )}

        {state && folders.length === 0 && (
          <p className="px-3 py-4 text-sm text-muted-foreground">No folders yet.</p>
        )}
      </div>
    </div>
  )
}
